/**
 * BRANCH (OUTLET) SELECTION — which tenant this request is being served for.
 *
 * ========================= HALF RECOVERY, HALF ADDITION =====================
 *
 * PART 1, `hostTenantId()` and the helpers above it, is FAITHFUL. The original
 * storefront resolves its tenant from the HOSTNAME and nothing else — module
 * 57528 of `_app-4ad30f8af265e8fd.js`:
 *
 *   function u(e){ let t=o(e); if(!t) return null;
 *                  let r=a(t).toLowerCase(); if(!r||"undefined"===r) return null;
 *                  let n=r.startsWith("www.")?r.slice(4):r, i=s(n);
 *                  return i ? (l(i)?n:i) : null }
 *   function p(e){ let t=u(e), r=u(n.env.NEXT_PUBLIC_CLIENT_DOMAIN);
 *                  return r&&(!t||l(s(t))) ? r : null!=t?t:r }
 *
 * — where `o` takes the first value of a possibly-repeated header, `a` strips
 * the scheme and any path, `s` strips the port (with IPv6 brackets handled) and
 * `l` is the localhost test. One deployment per branch, one domain per branch;
 * hence the reference dist being named `smashd-dha.eposmatic.com`. Those four
 * helpers are reproduced below under their own names.
 *
 * PART 2, everything from `BRANCH_COOKIE` down, is an ADDITION. The original
 * has no branch picker — searching its bundle for one turns up only Mongo
 * `$switch.branches` internals — because it never needed one: its host always
 * resolved. Ours is reached by bare IP (182.191.90.231:3002), where there is no
 * per-branch hostname to read, so without a picker the deployment can only ever
 * serve the one branch its .env names. The addition is kept in this one module,
 * separately named, and the recovered half above is not modified to accommodate
 * it.
 *
 * RESOLUTION ORDER (see `resolveBranch`): query -> host -> cookie -> env.
 */
import type { GetServerSidePropsContext } from "next";

import { fetchBranches } from "./api";
import { TENANT_ID } from "./config";
import type { Branch, BranchContextValue } from "@/types";

type BranchDirectory = Awaited<ReturnType<typeof fetchBranches>>;

// Branch membership/domains change rarely, but resolveBranch runs for every
// SSR navigation.  Without a cache, each page paid a complete API + Mongo round
// trip before the store and metadata requests could even start.  Keep this
// deliberately short so an admin change becomes visible quickly, while normal
// navigation through a store reuses the already-known directory.  The pending
// promise also prevents a burst of simultaneous SSR requests from stampeding
// the same endpoint after a process restart.
const BRANCH_DIRECTORY_TTL_MS = 30_000;
let branchDirectoryCache:
  | { tenantId: string; expiresAt: number; value: BranchDirectory }
  | null = null;
let branchDirectoryPending: Promise<BranchDirectory> | null = null;

const loadBranchDirectory = async (tenantId: string): Promise<BranchDirectory> => {
  const now = Date.now();
  if (
    branchDirectoryCache?.tenantId === tenantId &&
    branchDirectoryCache.expiresAt > now
  ) {
    return branchDirectoryCache.value;
  }
  if (branchDirectoryPending) return branchDirectoryPending;
  branchDirectoryPending = fetchBranches(tenantId)
    .then((value) => {
      branchDirectoryCache = {
        tenantId,
        expiresAt: Date.now() + BRANCH_DIRECTORY_TTL_MS,
        value,
      };
      return value;
    })
    .finally(() => {
      branchDirectoryPending = null;
    });
  return branchDirectoryPending;
};

/* -------------------------------------------------------------------------- *
 * PART 1 — the original's hostname resolution, reproduced.
 * -------------------------------------------------------------------------- */

/** `o` — first value of a header that may arrive repeated or comma-joined. */
const firstHeaderValue = (value?: string | string[]): string | null => {
  const raw = Array.isArray(value) ? value[0] : value;
  return raw?.split(",")[0]?.trim() || null;
};

/** `a` — drop the scheme and anything from the first slash on. */
const stripScheme = (value: string): string =>
  value.replace(/^https?:\/\//i, "").split("/")[0] || "";

/** `s` — drop the port. An IPv6 literal keeps its brackets' contents. */
const stripPort = (value: string): string => {
  if (value.startsWith("[")) {
    const end = value.indexOf("]");
    return end >= 0 ? value.slice(1, end) : value;
  }
  return value.split(":")[0] || value;
};

/** `l` — the loopback names the original treats as "no real domain". */
const isLoopback = (value: string): boolean =>
  value === "localhost" ||
  value === "127.0.0.1" ||
  value === "0.0.0.0" ||
  value === "::1";

/** `u` — a request host reduced to the domain the original matches on. */
export const normalizeHost = (
  value?: string | string[],
): string | null => {
  const first = firstHeaderValue(value);
  if (!first) return null;
  const host = stripScheme(first).toLowerCase();
  if (!host || host === "undefined") return null;
  const withoutWww = host.startsWith("www.") ? host.slice(4) : host;
  const bare = stripPort(withoutWww);
  if (!bare) return null;
  // Loopback keeps its port, so `localhost:3002` stays distinguishable — the
  // original does exactly this.
  return isLoopback(bare) ? withoutWww : bare;
};

/**
 * ADDITION, and the reason the picker is reachable at all on this deployment.
 *
 * An IP LITERAL is not a per-branch hostname. `182.191.90.231` is the box, not
 * a branch: two different tenants in this database both carry it in
 * `business_website`, so matching on it would resolve ambiguously AND would
 * suppress the picker on the only host the site is actually reached by.
 * Loopback is excluded for the same reason (and the original already treats it
 * as not-a-domain). Everything else is a real name and IS matched.
 */
const IPV4 = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;
export const isBranchResolvableHost = (host: string | null): boolean => {
  if (!host) return false;
  const bare = stripPort(host);
  if (isLoopback(bare)) return false;
  if (IPV4.test(bare)) return false;
  if (bare.includes(":")) return false; // IPv6 literal
  return bare.includes(".");
};

/**
 * Which branch, if any, this hostname names.
 *
 * Two data-driven rules, in order, both reading fields the API already serves:
 *
 *  1. EXACT DOMAIN — the host equals a branch's own `business_website`
 *     (tenants.business_website, the field the internal dashboard's Domains
 *     panel writes). This is the production path and the original's only rule.
 *
 *  2. BRANCH SUBDOMAIN — the host's first label equals a branch's tenant id, so
 *     `butt-karahi-dha.<anything>` serves DHA. This is an ADDITION. It is what
 *     lets one deployment answer for every branch the moment a wildcard DNS
 *     record exists, with no per-branch configuration and no list in the code,
 *     and it is testable today with nothing but a Host header.
 *
 * Returns null when the host names no branch, which is what hands the decision
 * on to the cookie.
 */
export const hostTenantId = (
  host: string | null,
  branches: Branch[],
): string | null => {
  if (!isBranchResolvableHost(host) || !host) return null;
  const bare = stripPort(host);

  // A per-branch domain is authoritative only when it identifies exactly one
  // branch.  Multi-branch storefronts deliberately put the SAME public domain
  // on every sibling (for example all Malang Jan outlets use
  // malangjan.zenvix.cloud).  Returning the first matching row there made a
  // user-selected branch work for one request and then silently snap back to
  // the alphabetically first branch on the next navigation, because host
  // resolution outranks the saved cookie.  Treat a shared domain as the brand
  // storefront instead, so the explicit query/cookie remains authoritative.
  const exactMatches = branches.filter((branch) => {
    const own = normalizeHost(branch.business_website);
    return Boolean(own && stripPort(own) === bare);
  });
  if (exactMatches.length === 1) return exactMatches[0]._id;
  if (exactMatches.length > 1) return null;

  const firstLabel = bare.split(".")[0];
  if (firstLabel) {
    const match = branches.find((branch) => branch._id === firstLabel);
    if (match) return match._id;
  }

  return null;
};

/* -------------------------------------------------------------------------- *
 * PART 2 — the ADDITION: a customer-chosen branch that survives SSR.
 * -------------------------------------------------------------------------- */

/**
 * WHY A COOKIE, and not localStorage or a path segment.
 *
 * Every page in this app is server-rendered (`getServerSideProps` on all of
 * them — that is why `server.js` can afford to lose Automatic Static
 * Optimization). The SERVER therefore has to know the branch before it renders,
 * or the first paint carries the default branch's name, colour, cover image and
 * prices and then swaps under the customer — the wrong-store flash. Of the
 * three candidates only a cookie is sent with the document request:
 *
 *   localStorage  invisible to the server. The page would render the default
 *                 branch every time and correct itself on hydration. Rejected.
 *   /b/<branch>/  visible to the server, but it changes every URL in the app,
 *                 every canonical, the sitemap, and every recovered route
 *                 constant. Far too invasive for an addition.
 *   cookie        sent on the document request, so `getServerSideProps` reads
 *                 it before the first byte. Chosen.
 *
 * SameSite=Lax (this is not a cross-site flow), Path=/ (every route needs it),
 * one year, and `Secure` whenever the request arrived over https. NOT HttpOnly:
 * the picker sets it from the browser, and it holds a public tenant id — there
 * is nothing in it to protect.
 */
export const BRANCH_COOKIE = "eposmatic_branch";

/** `?branch=<tenant id>` — an explicit override, and the no-DNS test handle. */
export const BRANCH_QUERY_PARAM = "branch";

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export const parseCookies = (header?: string): Record<string, string> => {
  const out: Record<string, string> = {};
  if (!header) return out;
  for (const part of header.split(";")) {
    const index = part.indexOf("=");
    if (index < 0) continue;
    const key = part.slice(0, index).trim();
    if (!key) continue;
    try {
      out[key] = decodeURIComponent(part.slice(index + 1).trim());
    } catch {
      out[key] = part.slice(index + 1).trim();
    }
  }
  return out;
};

export const branchCookieValue = (
  tenantId: string,
  secure: boolean,
): string =>
  `${BRANCH_COOKIE}=${encodeURIComponent(tenantId)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure ? "; Secure" : ""}`;

/** Browser side of the same cookie. */
export const writeBranchCookie = (tenantId: string): void => {
  if (typeof document === "undefined") return;
  document.cookie = branchCookieValue(
    tenantId,
    typeof window !== "undefined" && window.location.protocol === "https:",
  );
};

/**
 * Resolve the branch for ONE server request.
 *
 * `branches` is fetched from `/web/branches/:tenantId` using the build-time
 * `TENANT_ID` purely to name the BRAND — the endpoint answers with that
 * tenant's whole brand, so the deployment never has to carry a brand id. A
 * failed fetch is not fatal: the resolution falls all the way through to the
 * env default, which is exactly the pre-existing single-branch behaviour.
 *
 * Order, and the reason for it:
 *
 *   1. `?branch=`  an explicit request beats stored state, and is how host
 *                  resolution and branch switching are tested without DNS.
 *   2. HOST        a per-branch domain is unambiguous and is the ORIGINAL's
 *                  only rule; it must outrank a stale cookie, or a customer who
 *                  once picked Gulberg would be served Gulberg on
 *                  butt-karahi-dha.example.com. This is the production path.
 *   3. COOKIE      the customer's own choice on a host that names no branch —
 *                  i.e. this deployment, reached by IP.
 *   4. ENV         `NEXT_PUBLIC_TENANT_ID`, the build default. Unchanged
 *                  behaviour for a deployment that never shows the picker.
 *
 * Anything from 1 or 3 is VALIDATED against the visible branch list, so a
 * hand-edited cookie or a guessed query string cannot serve a delisted tenant,
 * another brand's tenant, or a tenant id that does not exist.
 */
export const resolveBranch = async (
  ctx: Pick<GetServerSidePropsContext, "req" | "query" | "res">,
): Promise<BranchContextValue> => {
  let branches: Branch[] = [];
  let brandName = "";
  try {
    const payload = await loadBranchDirectory(TENANT_ID);
    branches = payload.branches;
    brandName = payload.brand?.name ?? "";
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[branch] /web/branches failed; serving the env default:", error);
  }

  const known = (id?: string | null): string | null =>
    id && branches.some((branch) => branch._id === id) ? id : null;

  const queryRaw = ctx.query?.[BRANCH_QUERY_PARAM];
  const requested = known(
    Array.isArray(queryRaw) ? queryRaw[0] : (queryRaw as string | undefined),
  );

  const host = normalizeHost(ctx.req?.headers?.host);
  const fromHost = known(hostTenantId(host, branches));

  const cookies = parseCookies(ctx.req?.headers?.cookie);
  const fromCookie = known(cookies[BRANCH_COOKIE]);

  let tenantId = TENANT_ID;
  let source: BranchContextValue["source"] = "default";
  if (requested) {
    tenantId = requested;
    source = "query";
  } else if (fromHost) {
    tenantId = fromHost;
    source = "host";
  } else if (fromCookie) {
    tenantId = fromCookie;
    source = "cookie";
  }

  // An explicit `?branch=` is persisted immediately, so the choice survives the
  // customer's next navigation without the query string trailing along.
  if (source === "query" && ctx.res) {
    const secure =
      String(ctx.req?.headers?.["x-forwarded-proto"] ?? "").includes("https") ||
      Boolean((ctx.req?.socket as { encrypted?: boolean } | undefined)?.encrypted);
    ctx.res.setHeader("Set-Cookie", branchCookieValue(tenantId, secure));
  }

  return {
    tenantId,
    source,
    brandName,
    branches,
    /**
     * Show the picker up front only when NOTHING has chosen for this customer —
     * no query, no per-branch host, no stored choice — and there is genuinely
     * more than one branch to choose between. A single-branch brand, or a
     * deployment on a real per-branch domain, never sees it, which is what keeps
     * the recovered behaviour intact where it applies.
     */
    needsChoice: source === "default" && branches.length > 1,
  };
};
